import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Wallet, TrendingUp, TrendingDown, RefreshCw, Download } from 'lucide-react';

const formatPhp = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (s) => (s ? new Date(s).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const humanType = (t) => (t || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Tiny inline SVG sparkline. Takes an array of {day, sum} points and
 *  renders a smooth polyline sized to the given width/height. Renders
 *  nothing if there are fewer than 2 datapoints. */
function Sparkline({ series, width = 320, height = 48 }) {
  if (!series || series.length < 2) return null;
  const vals = series.map((p) => Number(p.sum) || 0);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 0);
  const range = max - min || 1;
  const step = width / (series.length - 1);
  const pts = series.map((p, i) => {
    const x = i * step;
    const y = height - ((Number(p.sum) - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = series[series.length - 1];
  const trend = last.sum >= 0 ? '#00E676' : '#FF6B6B';
  return (
    <div className="bg-[#000B18]/60 rounded-md p-3 border border-white/5" data-testid="reconcile-sparkline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-[#5C7599]">Last 30 days</span>
        <span className="text-[10px] text-[#A0B4CC] font-mono">
          {series.length} day{series.length === 1 ? '' : 's'} of activity
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        <defs>
          <linearGradient id="reconcile-spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={trend} stopOpacity="0.35" />
            <stop offset="100%" stopColor={trend} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline points={`0,${height} ${pts} ${width},${height}`} fill="url(#reconcile-spark-fill)" />
        <polyline points={pts} fill="none" stroke={trend} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// Escape a value for a CSV cell (double any embedded quotes + wrap in quotes
// if it contains a comma, quote, or newline).
function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * ToppedUpCreditsReconcileModal
 * ---------------------------------------------------------------
 * Explains the "Topped-up Credits" wallet-summary tile by listing
 * the underlying wallet_transactions (stock-trade credits, refunds,
 * admin adjustments…) that flowed into the spendable bucket.
 * Backed by GET /api/admin/topped-up-credits-breakdown.
 */
export function ToppedUpCreditsReconcileModal({ open, onOpenChange }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ grouped: [], transactions: [], total: 0, series_30d: [] });
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(
        `/api/admin/topped-up-credits-breakdown?skip=${skip}&limit=${LIMIT}`,
      );
      setData(res || { grouped: [], transactions: [], total: 0, series_30d: [] });
    } catch (e) {
      // silent; parent shows a toast if needed
    }
    setLoading(false);
  }, [api, skip]);

  const [exporting, setExporting] = useState(false);

  const exportCsv = useCallback(async () => {
    // Pull ALL rows in pages of 500 so the CSV is complete regardless of
    // what the user is currently paging through in the UI.
    setExporting(true);
    try {
      const CHUNK = 500;
      let s = 0;
      let all = [];
      // Cap at 50k rows for safety; larger admins can chunk manually.
      while (s < 50000) {
        const { data: res } = await api.get(
          `/api/admin/topped-up-credits-breakdown?skip=${s}&limit=${CHUNK}`,
        );
        const rows = res?.transactions || [];
        all = all.concat(rows);
        if (rows.length < CHUNK) break;
        s += CHUNK;
      }
      const header = ['created_at', 'type', 'amount_php', 'balance_after_php', 'user_id', 'owner_name', 'owner_email', 'note'];
      const lines = [header.join(',')];
      all.forEach((t) => {
        lines.push([
          csvCell(t.created_at),
          csvCell(t.type),
          csvCell(t.amount ?? ''),
          csvCell(t.balance_after ?? ''),
          csvCell(t.user_id),
          csvCell(t.owner_name || ''),
          csvCell(t.owner_email || ''),
          csvCell(t.note || ''),
        ].join(','));
      });
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `topped-up-credits_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } finally {
      setExporting(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) fetchData();
    else setSkip(0);
  }, [open, fetchData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#041A3B] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="topped-up-reconcile-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#FFEA00]" />
            Topped-up Credits Reconciliation
          </DialogTitle>
          <DialogDescription className="text-[#A0B4CC] text-xs">
            Wallet transactions credited outside of the referral programme —
            stock trades, refunds, admin adjustments — grouped by type with a
            paginated feed for audit.
          </DialogDescription>
        </DialogHeader>
        {loading && !data.transactions.length ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FFEA00]" /></div>
        ) : (
          <div className="space-y-5">
            {/* 30-day trend sparkline */}
            <Sparkline series={data.series_30d} />
            {/* Grouped summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="reconcile-grouped">
              {data.grouped.length === 0 && (
                <p className="col-span-full text-[#5C7599] text-sm text-center py-4">
                  No non-referral wallet transactions yet.
                </p>
              )}
              {data.grouped.map((g) => (
                <div key={g.type} className="p-3 rounded-md bg-[#000B18]/60 border border-white/5">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599]">{humanType(g.type)}</p>
                  <p className={`font-mono text-sm font-bold ${g.sum >= 0 ? 'text-[#00E676]' : 'text-[#FF6B6B]'}`}>
                    {formatPhp(g.sum)}
                  </p>
                  <p className="text-[10px] text-[#5C7599]">{g.count} txn{g.count === 1 ? '' : 's'}</p>
                </div>
              ))}
            </div>

            {/* Per-transaction feed */}
            <div className="rounded-md border border-white/10 overflow-hidden">
              <div className="bg-[#000B18]/60 px-3 py-2 flex items-center justify-between gap-2">
                <p className="text-[#A0B4CC] text-xs font-medium">
                  Latest transactions ({data.total.toLocaleString()} total)
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm"
                    onClick={exportCsv}
                    disabled={exporting || data.total === 0}
                    className="text-[#00E676] hover:bg-[#00E676]/10 h-7 px-2"
                    data-testid="reconcile-export-csv"
                  >
                    {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                    <span className="text-[11px]">CSV</span>
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={fetchData}
                    className="text-[#00E5FF] hover:bg-[#00E5FF]/10 h-7 px-2"
                    data-testid="reconcile-refresh"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <ul className="divide-y divide-white/5" data-testid="reconcile-txn-list">
                {data.transactions.length === 0 && (
                  <li className="p-4 text-center text-[#5C7599] text-sm">No transactions.</li>
                )}
                {data.transactions.map((t) => (
                  <li key={t.id || `${t.user_id}-${t.created_at}`} className="p-3 flex items-start gap-3 hover:bg-white/5">
                    <div className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${t.amount >= 0 ? 'bg-[#00E676]/15 text-[#00E676]' : 'bg-[#FF6B6B]/15 text-[#FF6B6B]'}`}>
                      {t.amount >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-x-2">
                        <span className="text-white text-sm font-medium">{humanType(t.type)}</span>
                        <span className={`font-mono text-sm font-bold ${t.amount >= 0 ? 'text-[#00E676]' : 'text-[#FF6B6B]'}`}>
                          {t.amount >= 0 ? '+' : ''}{formatPhp(t.amount)}
                        </span>
                      </div>
                      <p className="text-[#A0B4CC] text-xs truncate">
                        {t.owner_name || t.owner_email || t.user_id?.slice(0, 8)}
                        {t.owner_email && t.owner_name ? ` · ${t.owner_email}` : ''}
                      </p>
                      {t.note && <p className="text-[#5C7599] text-[11px] break-words">{t.note}</p>}
                    </div>
                    <p className="text-[10px] text-[#5C7599] whitespace-nowrap">{formatDate(t.created_at)}</p>
                  </li>
                ))}
              </ul>
              {/* Pagination */}
              {data.total > LIMIT && (
                <div className="flex items-center justify-between px-3 py-2 bg-[#000B18]/60 text-xs text-[#A0B4CC]">
                  <span>{skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" disabled={skip === 0}
                      onClick={() => setSkip(Math.max(0, skip - LIMIT))}
                      className="text-[#00E5FF] h-7 px-2" data-testid="reconcile-prev">
                      Prev
                    </Button>
                    <Button variant="ghost" size="sm" disabled={skip + LIMIT >= data.total}
                      onClick={() => setSkip(skip + LIMIT)}
                      className="text-[#00E5FF] h-7 px-2" data-testid="reconcile-next">
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ToppedUpCreditsReconcileModal;
